/**
 * Application constants
 * Centralized configuration for roles, labels, and other app-wide constants
 */

import type { UserRole, Language } from '../types';

/**
 * Role priority order (highest to lowest)
 * Used for determining primary role when user has multiple roles
 */
export const ROLE_PRIORITY: readonly UserRole[] = [
  'admin',
  'lecturer',
  'staff',
  'student',
  'alumni',
  'student_applicant',
  'guest',
] as const;

/**
 * Human-readable labels for each role
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  lecturer: 'Lecturer',
  staff: 'Staff',
  student: 'Student',
  alumni: 'Alumni',
  student_applicant: 'Applicant',
  guest: 'Guest',
} as const;

/**
 * Portal titles for each role (shown in sidebar)
 */
export const PORTAL_TITLES: Record<UserRole, string> = {
  admin: 'Admin Portal',
  lecturer: 'Lecturer Portal',
  staff: 'Staff Portal',
  student: 'Student Portal',
  alumni: 'Alumni Portal',
  student_applicant: 'Applicant Portal',
  guest: 'Guest Portal',
} as const;

/**
 * Dashboard paths for each role
 */
export const DASHBOARD_PATHS: Record<UserRole, string> = {
  admin: '/admin/dashboard',
  lecturer: '/lecturer/dashboard',
  staff: '/staff/dashboard',
  student: '/student/dashboard',
  alumni: '/alumni/dashboard',
  student_applicant: '/student_applicant/dashboard',
  guest: '/guest/dashboard',
} as const;

/**
 * Get the primary role from an array of roles based on priority
 */
export const getPrimaryRole = (roles: UserRole[]): UserRole => {
  for (const priorityRole of ROLE_PRIORITY) {
    if (roles.includes(priorityRole)) {
      return priorityRole;
    }
  }
  return 'guest';
};

/**
 * Get dashboard path for a given role
 */
export const getDashboardPath = (role: UserRole): string => {
  return DASHBOARD_PATHS[role] || DASHBOARD_PATHS.guest;
};

/**
 * Get human-readable label for a role
 */
export const getRoleLabel = (role: UserRole): string => {
  return ROLE_LABELS[role] || 'Unknown';
};

/**
 * Get portal title for a role
 */
export const getPortalTitle = (role: UserRole): string => {
  return PORTAL_TITLES[role] || 'Portal';
};

/**
 * Academic year and semester constants
 */
export const CURRENT_ACADEMIC_YEAR = '2024/2025';

/**
 * Days of the week
 */
export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type DayOfWeek = typeof DAYS_OF_WEEK[number];

/**
 * Letter grades with their corresponding GPA values
 */
export const GRADE_SCALE: Record<string, number> = {
  'A': 4.0,
  'A-': 3.7,
  'B+': 3.3,
  'B': 3.0,
  'B-': 2.7,
  'C+': 2.3,
  'C': 2.0,
  'C-': 1.7,
  'D+': 1.3,
  'D': 1.0,
  'E': 0.0,
} as const;

/**
 * Attendance status options
 */
export const ATTENDANCE_STATUS = {
  PRESENT: 'Hadir',
  PERMISSION: 'Izin',
  SICK: 'Sakit',
  ABSENT: 'Alfa',
  LATE: 'Terlambat',
  SUBSTITUTE: 'Pengganti',
} as const;

export type AttendanceStatus = typeof ATTENDANCE_STATUS[keyof typeof ATTENDANCE_STATUS];

/**
 * Language constants
 */
export const SUPPORTED_LANGUAGES: Language[] = ['id', 'en'] as const;

export const LANGUAGE_LABELS: Record<Language, string> = {
  id: 'Bahasa Indonesia',
  en: 'English',
} as const;

export const DEFAULT_LANGUAGE: Language = 'id' as const;

/**
 * Get language label for display
 */
export const getLanguageLabel = (lang: Language): string => {
  return LANGUAGE_LABELS[lang] || lang;
};
